// Hand-written, never generated. Suspense-for-data cache for
// src/pages/blog/BlogIndexPage.jsx and BlogArticlePage.jsx -- see
// docs/superpowers/specs/2026-08-04-blog-architecture-design.md §6.3 for
// the full reasoning. Two responsibilities:
//
// 1. readResource(key, loader): the standard pre-`use()`-hook
//    Suspense-for-data pattern. A cache miss calls loader() (one of the
//    generated functions in loaders.generated.js /
//    manifestLoaders.generated.js), stores the pending promise, and
//    throws it -- caught by the existing [data-route-suspense-boundary]
//    Suspense boundary AnimatedRoutes already renders (Sprint 8.2), no
//    new boundary needed.
//
// 2. Seeding: at module load, synchronously check for a
//    #__PRERENDERED_BLOG_RESOURCES__ <script> (embedded by
//    scripts/build/prerender.js, same pattern as src/i18n.js's existing
//    __PRERENDERED_I18N__ read) and pre-populate the cache with
//    already-resolved entries. On the prerendered page's first real
//    hydration render, readResource for an embedded key returns
//    synchronously -- never suspends, never diverges from what the
//    static HTML already shows.
const cache = new Map();

function seedFromEmbeddedScript() {
  if (typeof document === 'undefined') return;
  const el = document.getElementById('__PRERENDERED_BLOG_RESOURCES__');
  if (!el) return;
  try {
    const seeded = JSON.parse(el.textContent);
    for (const [key, data] of Object.entries(seeded)) {
      cache.set(key, { status: 'success', data });
    }
  } catch {
    // Malformed embedded JSON -- fall through to the normal async path
    // for every key instead of crashing the whole page.
  }
}
seedFromEmbeddedScript();

/**
 * @param {string} key - cache key, e.g. "manifest:ru" or "post:ru:some-slug"
 * @param {() => Promise<{default: any} | any>} loader - one of the
 *   generated loader functions (dynamic `import()` of a JSON module)
 * @returns {any} the resolved resource
 * @throws the pending promise (caught by the nearest Suspense boundary)
 *   or the rejection error, if not yet resolved
 */
export function readResource(key, loader) {
  let entry = cache.get(key);

  if (!entry) {
    entry = { status: 'pending' };
    entry.promise = loader()
      .then((mod) => {
        const data = mod && mod.default !== undefined ? mod.default : mod;
        entry.status = 'success';
        entry.data = data;
        if (typeof window !== 'undefined') {
          window.__BLOG_RESOURCES__ = window.__BLOG_RESOURCES__ || {};
          window.__BLOG_RESOURCES__[key] = data;
        }
      })
      .catch((err) => {
        entry.status = 'error';
        entry.error = err;
      });
    cache.set(key, entry);
  }

  if (entry.status === 'pending') throw entry.promise;
  if (entry.status === 'error') throw entry.error;
  return entry.data;
}

/**
 * Empties the entire cache. Added for BlogErrorBoundary's retry action
 * (src/components/blog/BlogErrorBoundary.jsx): once an entry's status is
 * 'error', readResource() re-throws that SAME cached error on every call,
 * forever -- remounting whatever component calls readResource() does NOT
 * clear it, since `cache` above is a module-level singleton, not
 * component state. A retry handler must call this first so the next
 * readResource() call for the same key is a genuine cache miss and starts
 * a fresh loader() attempt, instead of instantly re-throwing the stale
 * rejection. Without it, "retry" would only reset the boundary's local
 * hasError flag -- cosmetic, not a real retry.
 *
 * Deliberately whole-cache, not single-key: the only real caller is a
 * boundary-level retry after an uncaught error, where we don't reliably
 * know which key(s) were involved (a manifest read and a post read can
 * both be in-flight/cached for the same page). Clearing everything is
 * simple and safe -- worst case, a few already-successful entries get
 * refetched -- and avoids threading key-tracking through the error
 * boundary.
 */
export function clearAllResources() {
  cache.clear();
}